import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

// Keep the preferred desktop width independently of temporary viewport limits.
// Values are read before the first render and written only when a gesture ends.
export default function usePanelWidth({ storageKey, defaultWidth, min, max, breakpoint, contentMin = 320, gap = 16, enabled = true }) {
  const panelRef = useRef(null), sessionRef = useRef(null);
  const [preferredWidth, setPreferredWidth] = useState(() => {
    try {
      const saved = window.localStorage.getItem(storageKey);
      const value = saved === null || saved.trim() === '' ? NaN : Number(saved);
      return Number.isFinite(value) ? clamp(value, min, max) : defaultWidth;
    } catch { return defaultWidth; }
  });
  const preferredRef = useRef(preferredWidth);
  const [limit, setLimit] = useState(max), [resizing, setResizing] = useState(false);
  const limitRef = useRef(limit); limitRef.current = limit;
  const width = Math.min(preferredWidth, limit);

  const persist = useCallback(() => {
    try { window.localStorage.setItem(storageKey, String(preferredRef.current)); } catch { /* Browser storage may be unavailable. */ }
  }, [storageKey]);
  const changeWidth = useCallback((next) => {
    const value = Math.round(clamp(next, min, limitRef.current));
    preferredRef.current = value; setPreferredWidth(value);
  }, [min]);
  const finish = useCallback((updateState = true) => {
    const session = sessionRef.current;
    if (!session) return;
    sessionRef.current = null;
    document.body.style.cursor = session.cursor;
    document.body.style.userSelect = session.userSelect;
    if (session.target.hasPointerCapture?.(session.pointerId)) session.target.releasePointerCapture(session.pointerId);
    if (updateState) setResizing(false);
    persist();
  }, [persist]);

  useLayoutEffect(() => {
    if (!enabled) return;
    const parent = panelRef.current?.parentElement;
    if (!parent) return;
    const measure = () => {
      if (window.innerWidth < breakpoint) { finish(); setLimit(max); return; }
      setLimit(Math.max(min, Math.min(max, Math.floor(parent.getBoundingClientRect().width - contentMin - gap))));
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(parent); window.addEventListener('resize', measure);
    return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
  }, [breakpoint, contentMin, gap, max, min, finish, enabled]);
  useEffect(() => {
    const onBlur = () => finish();
    window.addEventListener('blur', onBlur);
    return () => { window.removeEventListener('blur', onBlur); finish(false); };
  }, [finish]);

  const separatorProps = {
    role: 'separator', tabIndex: 0, 'aria-orientation': 'vertical',
    'aria-valuemin': min, 'aria-valuemax': limit, 'aria-valuenow': width,
    'aria-valuetext': `${width} px`, 'data-resizing': resizing ? 'true' : 'false',
    onPointerDown: (event) => {
      if (window.innerWidth < breakpoint || sessionRef.current || (event.pointerType === 'mouse' && event.button !== 0)) return;
      event.preventDefault(); event.currentTarget.focus();
      sessionRef.current = { startX: event.clientX, startWidth: width, target: event.currentTarget, pointerId: event.pointerId, cursor: document.body.style.cursor, userSelect: document.body.style.userSelect };
      event.currentTarget.setPointerCapture(event.pointerId);
      document.body.style.cursor = 'col-resize'; document.body.style.userSelect = 'none'; setResizing(true);
    },
    onPointerMove: (event) => {
      const session = sessionRef.current;
      if (session?.pointerId === event.pointerId) changeWidth(session.startWidth + event.clientX - session.startX);
    },
    onPointerUp: (event) => { if (sessionRef.current?.pointerId === event.pointerId) finish(); },
    onPointerCancel: (event) => { if (sessionRef.current?.pointerId === event.pointerId) finish(); },
    onLostPointerCapture: (event) => { if (sessionRef.current?.pointerId === event.pointerId) finish(); },
    onDoubleClick: () => { changeWidth(defaultWidth); persist(); },
    onKeyDown: (event) => {
      const step = event.shiftKey ? 50 : 10;
      const next = { ArrowLeft: width - step, ArrowRight: width + step, Home: min, End: limit }[event.key];
      if (next === undefined) return;
      event.preventDefault(); changeWidth(next); persist();
    },
  };
  return { panelRef, width, resizing, separatorProps };
}
